import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Globe, Plane, Clock, Loader2, MapPin, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';

const TIMEZONES = [
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)', offset: '+02:00' },
  { value: 'America/New_York', label: 'New York (EST)', offset: '-05:00' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST)', offset: '-08:00' },
  { value: 'America/Chicago', label: 'Chicago (CST)', offset: '-06:00' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)', offset: '-06:00' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)', offset: '-03:00' },
  { value: 'Europe/London', label: 'London (GMT)', offset: '+00:00' },
  { value: 'Europe/Paris', label: 'Paris (CET)', offset: '+01:00' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)', offset: '+08:00' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: '+09:00' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)', offset: '+09:00' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)', offset: '+04:00' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT)', offset: '+11:00' },
];

export default function TimezonesTab({ meetingId, meeting, members }) {
  const { api, member: currentMember } = useAuth();
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchOverrides = useCallback(async () => {
    try {
      const response = await api().get(`/meetings/${meetingId}/timezone-overrides`);
      setOverrides(response.data);
    } catch (error) {
      console.error('Failed to fetch timezone overrides:', error);
    } finally {
      setLoading(false);
    }
  }, [api, meetingId]);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  const myOverride = useMemo(() => {
    return overrides.find(o => o.member_id === currentMember?.id);
  }, [overrides, currentMember]);

  const membersWithTimezones = useMemo(() => {
    return members.map(m => {
      const override = overrides.find(o => o.member_id === m.id);
      return {
        ...m,
        activeTimezone: override?.override_timezone || m.home_timezone,
        isTraveling: !!override,
      };
    });
  }, [members, overrides]);

  const handleSetOverride = async () => {
    if (!selectedTimezone) {
      toast.error('Please select a timezone');
      return;
    }

    setSaving(true);
    try {
      await api().post(`/meetings/${meetingId}/timezone-override?override_timezone=${selectedTimezone}`);
      await fetchOverrides();
      setDialogOpen(false);
      toast.success('Timezone override set');
    } catch (error) {
      toast.error('Failed to set timezone override');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveOverride = async () => {
    setSaving(true);
    try {
      await api().delete(`/meetings/${meetingId}/timezone-override`);
      await fetchOverrides();
      toast.success('Timezone override removed');
    } catch (error) {
      toast.error('Failed to remove override');
    } finally {
      setSaving(false);
    }
  };

  const getTimeInZone = (timezone) => {
    try {
      return new Date().toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '--:--';
    }
  };

  const getTimezoneLabel = (tz) => {
    const found = TIMEZONES.find(t => t.value === tz);
    return found?.label || tz?.replace(/_/g, ' ') || 'Unknown';
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isNightTime = (timezone) => {
    try {
      const hour = new Date().toLocaleString('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        hour12: false,
      });
      const h = parseInt(hour);
      return h < 6 || h >= 22;
    } catch {
      return false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timezones-tab">
      {/* Reference timezone */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Meeting Reference Timezone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold">{getTimezoneLabel(meeting.reference_timezone)}</p>
              <p className="text-sm text-muted-foreground">
                Current time: {getTimeInZone(meeting.reference_timezone)}
              </p>
            </div>
            <Badge variant="outline" className="font-mono">
              {TIMEZONES.find(t => t.value === meeting.reference_timezone)?.offset || ''}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* My timezone */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Your Timezone for This Meeting
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold">
                  {getTimezoneLabel(myOverride?.override_timezone || currentMember?.home_timezone)}
                </p>
                {myOverride && (
                  <Badge variant="secondary" className="gap-1">
                    <Plane className="h-3 w-3" />
                    Traveling
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Your time: {getTimeInZone(myOverride?.override_timezone || currentMember?.home_timezone)}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="set-override-btn">
                  <Plane className="h-4 w-4" />
                  {myOverride ? 'Change Travel Timezone' : 'I\'m Traveling'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Set Travel Timezone</DialogTitle>
                  <DialogDescription>
                    Override your timezone for this meeting while traveling. This won't change your home timezone.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label htmlFor="timezone">Select your current timezone</Label>
                  <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                    <SelectTrigger className="mt-2" data-testid="timezone-override-select">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label} ({tz.offset})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSetOverride} disabled={saving} data-testid="confirm-override-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Set Timezone
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {myOverride && (
              <Button 
                variant="ghost" 
                onClick={handleRemoveOverride}
                disabled={saving}
                data-testid="remove-override-btn"
              >
                Remove Override
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* All members */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">All Members' Timezones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {membersWithTimezones.map((m, index) => (
              <div 
                key={m.id} 
                className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                data-testid={`member-timezone-${index}`}
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={m.photo_url} alt={m.name} />
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {getInitials(m.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{m.name}</span>
                      {m.isTraveling && (
                        <Badge variant="secondary" className="gap-1 text-xs">
                          <Plane className="h-3 w-3" />
                          Traveling
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      <span>{getTimezoneLabel(m.activeTimezone)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isNightTime(m.activeTimezone) ? (
                    <Moon className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Sun className="h-4 w-4 text-yellow-500" />
                  )}
                  <span className="font-mono text-sm min-w-[70px] text-right">
                    {getTimeInZone(m.activeTimezone)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
